import * as ImagePicker from 'expo-image-picker';
import * as DocumentPicker from 'expo-document-picker';
import * as Location from 'expo-location';
import * as Contacts from 'expo-contacts';
import { Audio } from 'expo-av';
import { Alert, Platform } from 'react-native';

export interface MediaResult {
  uri: string;
  type: 'image' | 'video' | 'document' | 'location';
  fileName?: string;
  fileSize?: number;
  width?: number;
  height?: number;
  mimeType?: string;
  // Location specific fields
  latitude?: number;
  longitude?: number;
  address?: string;
}

export class MediaService {
  // Fallback implementations for Expo Go compatibility
  
  static async pickImageFromGallery(): Promise<MediaResult | null> {
    console.log('🖼️ [MediaService] Picking image from gallery...');
    
    try {
      // Try native picker first
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: 'images',
        allowsEditing: true,
        aspect: [4, 3],
        quality: 0.8,
      });

      if (result.canceled) {
        console.log('🖼️ [MediaService] Image selection canceled');
        return null;
      }

      const mediaResult: MediaResult = {
        uri: result.assets[0].uri,
        type: 'image',
        fileName: result.assets[0].fileName || `image_${Date.now()}.jpg`,
        fileSize: result.assets[0].fileSize || 0,
        width: result.assets[0].width,
        height: result.assets[0].height,
        mimeType: result.assets[0].type || 'image/jpeg',
      };

      console.log('🖼️ [MediaService] Image selected successfully:', mediaResult);
      return mediaResult;
    } catch (error) {
      console.log('🖼️ [MediaService] Native picker failed, using fallback:', error);
      return this.showFallbackImagePicker();
    }
  }

  static async captureImage(): Promise<MediaResult | null> {
    console.log('📷 [MediaService] Capturing image...');
    
    try {
      // Try native camera first
      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: 'images',
        allowsEditing: true,
        aspect: [4, 3],
        quality: 0.8,
      });

      if (result.canceled) {
        console.log('📷 [MediaService] Camera capture canceled');
        return null;
      }

      const mediaResult: MediaResult = {
        uri: result.assets[0].uri,
        type: 'image',
        fileName: result.assets[0].fileName || `camera_${Date.now()}.jpg`,
        fileSize: result.assets[0].fileSize || 0,
        width: result.assets[0].width,
        height: result.assets[0].height,
        mimeType: result.assets[0].type || 'image/jpeg',
      };

      console.log('📷 [MediaService] Image captured successfully:', mediaResult);
      return mediaResult;
    } catch (error) {
      console.log('📷 [MediaService] Native camera failed, using fallback:', error);
      return this.showFallbackImagePicker();
    }
  }

  static async pickVideo(): Promise<MediaResult | null> {
    console.log('🎥 [MediaService] Picking video from gallery...');
    
    try {
      // Try native video picker first
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: 'videos',
        allowsEditing: false,
        quality: 0.8,
      });

      if (result.canceled) {
        console.log('🎥 [MediaService] Video selection canceled');
        return null;
      }

      const mediaResult: MediaResult = {
        uri: result.assets[0].uri,
        type: 'video',
        fileName: result.assets[0].fileName || `video_${Date.now()}.mp4`,
        fileSize: result.assets[0].fileSize || 0,
        width: result.assets[0].width,
        height: result.assets[0].height,
        mimeType: result.assets[0].type || 'video/mp4',
      };

      console.log('🎥 [MediaService] Video selected successfully:', mediaResult);
      return mediaResult;
    } catch (error) {
      console.log('🎥 [MediaService] Native video picker failed:', error);
      Alert.alert(
        'Unable to Access Videos',
        'Failed to select video. Please try again.',
        [{ text: 'OK' }]
      );
      return null;
    }
  }

  static async captureVideo(): Promise<MediaResult | null> {
    console.log('🎥 [MediaService] Capturing video...');
    
    try {
      // Try native camera for video
      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: 'videos',
        allowsEditing: false,
        quality: 0.8,
      });

      if (result.canceled) {
        console.log('🎥 [MediaService] Video capture canceled');
        return null;
      }

      const mediaResult: MediaResult = {
        uri: result.assets[0].uri,
        type: 'video',
        fileName: result.assets[0].fileName || `camera_video_${Date.now()}.mp4`,
        fileSize: result.assets[0].fileSize || 0,
        width: result.assets[0].width,
        height: result.assets[0].height,
        mimeType: result.assets[0].type || 'video/mp4',
      };

      console.log('🎥 [MediaService] Video captured successfully:', mediaResult);
      return mediaResult;
    } catch (error) {
      console.log('🎥 [MediaService] Native video capture failed:', error);
      Alert.alert(
        'Unable to Record Video',
        'Failed to record video. Please try again.',
        [{ text: 'OK' }]
      );
      return null;
    }
  }

  static async pickDocument(): Promise<MediaResult | null> {
    console.log('📄 [MediaService] Picking document...');
    
    try {
      // Try native document picker first
      const result = await DocumentPicker.getDocumentAsync({
        type: '*/*',
        copyToCacheDirectory: true,
      });

      if (result.canceled) {
        console.log('📄 [MediaService] Document selection canceled');
        return null;
      }

      const mediaResult: MediaResult = {
        uri: result.assets[0].uri,
        type: 'document',
        fileName: result.assets[0].name || `document_${Date.now()}.pdf`,
        fileSize: result.assets[0].size || 0,
        mimeType: result.assets[0].mimeType || 'application/pdf',
      };

      console.log('📄 [MediaService] Document selected successfully:', mediaResult);
      return mediaResult;
    } catch (error) {
      console.log('📄 [MediaService] Native document picker failed, using fallback:', error);
      return this.showFallbackDocumentPicker();
    }
  }

  static async getCurrentLocation(): Promise<MediaResult | null> {
    console.log('📍 [MediaService] Getting location...');
    
    try {
      // Try native location first
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted') {
        console.log('📍 [MediaService] Location permission denied');
        return this.showFallbackLocationPicker();
      }

      const location = await Location.getCurrentPositionAsync({});
      const address = await Location.reverseGeocodeAsync({
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
      });

      const mediaResult: MediaResult = {
        uri: `geo:${location.coords.latitude},${location.coords.longitude}`,
        type: 'location',
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
        address: address[0] ? `${address[0].city}, ${address[0].region}` : 'Current Location',
        fileName: 'location.json',
        fileSize: 100,
        mimeType: 'application/json',
      };

      console.log('📍 [MediaService] Location obtained successfully:', mediaResult);
      return mediaResult;
    } catch (error) {
      console.log('📍 [MediaService] Native location failed, using fallback:', error);
      return this.showFallbackLocationPicker();
    }
  }

  // Enhanced fallback picker implementations
  // Note: Fallbacks disabled on iOS to comply with App Store guidelines
  static async showFallbackImagePicker(): Promise<MediaResult | null> {
    // On iOS, don't show fallback - return null to prevent App Store rejection
    if (Platform.OS === 'ios') {
      console.log('🖼️ [MediaService] Fallback picker disabled on iOS');
      Alert.alert(
        'Unable to Access Media',
        'Please enable camera and photo library permissions in Settings to use this feature.',
        [{ text: 'OK' }]
      );
      return null;
    }
    
    return new Promise((resolve) => {
      Alert.alert(
        'Select Image',
        'Choose an image source:',
        [
          { text: 'Cancel', onPress: () => resolve(null) },
          { 
            text: 'Sample Image 1', 
            onPress: () => {
              const mockResult: MediaResult = {
                uri: 'https://picsum.photos/800/600',
                type: 'image',
                fileName: `sample_image_1_${Date.now()}.jpg`,
                fileSize: 1024 * 150,
                width: 800,
                height: 600,
                mimeType: 'image/jpeg',
              };
              console.log('🖼️ [MediaService] Using sample image 1:', mockResult);
              resolve(mockResult);
            }
          },
          { 
            text: 'Sample Image 2', 
            onPress: () => {
              const mockResult: MediaResult = {
                uri: 'https://picsum.photos/600/800',
                type: 'image',
                fileName: `sample_image_2_${Date.now()}.jpg`,
                fileSize: 1024 * 120,
                width: 600,
                height: 800,
                mimeType: 'image/jpeg',
              };
              console.log('🖼️ [MediaService] Using sample image 2:', mockResult);
              resolve(mockResult);
            }
          },
          { 
            text: 'Custom URL', 
            onPress: () => {
              Alert.prompt(
                'Enter Image URL',
                'Please enter a valid image URL:',
                [
                  { text: 'Cancel', onPress: () => resolve(null) },
                  { 
                    text: 'Use URL', 
                    onPress: (text: string | undefined) => {
                      if (text && text.trim()) {
                        const mockResult: MediaResult = {
                          uri: text.trim(),
                          type: 'image',
                          fileName: `custom_image_${Date.now()}.jpg`,
                          fileSize: 1024 * 100,
                          width: 800,
                          height: 600,
                          mimeType: 'image/jpeg',
                        };
                        console.log('🖼️ [MediaService] Using custom image URL:', mockResult);
                        resolve(mockResult);
                      } else {
                        resolve(null);
                      }
                    }
                  }
                ],
                'plain-text',
                'https://picsum.photos/800/600'
              );
            }
          }
        ]
      );
    });
  }

  static async showFallbackDocumentPicker(): Promise<MediaResult | null> {
    // On iOS, don't show fallback - return null to comply with App Store guidelines
    if (Platform.OS === 'ios') {
      console.log('📄 [MediaService] Fallback picker disabled on iOS');
      Alert.alert(
        'Unable to Access Documents',
        'Please enable document access permissions in Settings to use this feature.',
        [{ text: 'OK' }]
      );
      return null;
    }
    
    return new Promise((resolve) => {
      Alert.alert(
        'Select Document',
        'Choose a document type:',
        [
          { text: 'Cancel', onPress: () => resolve(null) },
          { 
            text: 'PDF Document', 
            onPress: () => {
              const mockResult: MediaResult = {
                uri: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
                type: 'document',
                fileName: `sample_document_${Date.now()}.pdf`,
                fileSize: 1024 * 500,
                mimeType: 'application/pdf',
              };
              console.log('📄 [MediaService] Using PDF document:', mockResult);
              resolve(mockResult);
            }
          },
          { 
            text: 'Text File', 
            onPress: () => {
              const mockResult: MediaResult = {
                uri: 'https://raw.githubusercontent.com/microsoft/vscode/main/README.md',
                type: 'document',
                fileName: `sample_text_${Date.now()}.txt`,
                fileSize: 1024 * 50,
                mimeType: 'text/plain',
              };
              console.log('📄 [MediaService] Using text file:', mockResult);
              resolve(mockResult);
            }
          },
          { 
            text: 'Custom URL', 
            onPress: () => {
              Alert.prompt(
                'Enter Document URL',
                'Please enter a valid document URL:',
                [
                  { text: 'Cancel', onPress: () => resolve(null) },
                  { 
                    text: 'Use URL', 
                    onPress: (text: string | undefined) => {
                      if (text && text.trim()) {
                        const mockResult: MediaResult = {
                          uri: text.trim(),
                          type: 'document',
                          fileName: `custom_document_${Date.now()}.pdf`,
                          fileSize: 1024 * 300,
                          mimeType: 'application/pdf',
                        };
                        console.log('📄 [MediaService] Using custom document URL:', mockResult);
                        resolve(mockResult);
                      } else {
                        resolve(null);
                      }
                    }
                  }
                ],
                'plain-text',
                'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf'
              );
            }
          }
        ]
      );
    });
  }

  static async showFallbackLocationPicker(): Promise<MediaResult | null> {
    // On iOS, don't show fallback - return null to comply with App Store guidelines
    if (Platform.OS === 'ios') {
      console.log('📍 [MediaService] Fallback picker disabled on iOS');
      Alert.alert(
        'Unable to Access Location',
        'Please enable location permissions in Settings to share your location.',
        [{ text: 'OK' }]
      );
      return null;
    }
    
    return new Promise((resolve) => {
      Alert.alert(
        'Select Location',
        'Choose a location:',
        [
          { text: 'Cancel', onPress: () => resolve(null) },
          { 
            text: 'San Francisco, CA', 
            onPress: () => {
              const mockResult: MediaResult = {
                uri: 'geo:37.7749,-122.4194',
                type: 'location',
                latitude: 37.7749,
                longitude: -122.4194,
                address: 'San Francisco, CA, USA',
                fileName: 'location_sf.json',
                fileSize: 100,
                mimeType: 'application/json',
              };
              console.log('📍 [MediaService] Using San Francisco location:', mockResult);
              resolve(mockResult);
            }
          },
          { 
            text: 'New York, NY', 
            onPress: () => {
              const mockResult: MediaResult = {
                uri: 'geo:40.7128,-74.0060',
                type: 'location',
                latitude: 40.7128,
                longitude: -74.0060,
                address: 'New York, NY, USA',
                fileName: 'location_ny.json',
                fileSize: 100,
                mimeType: 'application/json',
              };
              console.log('📍 [MediaService] Using New York location:', mockResult);
              resolve(mockResult);
            }
          },
          { 
            text: 'London, UK', 
            onPress: () => {
              const mockResult: MediaResult = {
                uri: 'geo:51.5074,-0.1278',
                type: 'location',
                latitude: 51.5074,
                longitude: -0.1278,
                address: 'London, UK',
                fileName: 'location_london.json',
                fileSize: 100,
                mimeType: 'application/json',
              };
              console.log('📍 [MediaService] Using London location:', mockResult);
              resolve(mockResult);
            }
          },
          { 
            text: 'Custom Coordinates', 
            onPress: () => {
              Alert.prompt(
                'Enter Coordinates',
                'Please enter coordinates in format: latitude,longitude',
                [
                  { text: 'Cancel', onPress: () => resolve(null) },
                  { 
                    text: 'Use Coordinates', 
                    onPress: (text: string | undefined) => {
                      if (text && text.trim()) {
                        const [lat, lng] = text.split(',').map(coord => parseFloat(coord.trim()));
                        if (!isNaN(lat) && !isNaN(lng)) {
                          const mockResult: MediaResult = {
                            uri: `geo:${lat},${lng}`,
                            type: 'location',
                            latitude: lat,
                            longitude: lng,
                            address: `Custom Location (${lat}, ${lng})`,
                            fileName: 'location_custom.json',
                            fileSize: 100,
                            mimeType: 'application/json',
                          };
                          console.log('📍 [MediaService] Using custom location:', mockResult);
                          resolve(mockResult);
                        } else {
                          Alert.alert('Invalid Format', 'Please enter valid coordinates like: 37.7749,-122.4194');
                          resolve(null);
                        }
                      } else {
                        resolve(null);
                      }
                    }
                  }
                ],
                'plain-text',
                '37.7749,-122.4194'
              );
            }
          }
        ]
      );
    });
  }

  // Alias for compatibility
  static async takePhoto(): Promise<MediaResult | null> {
    return this.captureImage();
  }

  // Permission check (real permissions when available)
  static async checkPermissions(): Promise<{
    camera: boolean;
    mediaLibrary: boolean;
    location: boolean;
    microphone: boolean;
    contacts: boolean;
  }> {
    try {
      const cameraStatus = await ImagePicker.getCameraPermissionsAsync();
      const mediaStatus = await ImagePicker.getMediaLibraryPermissionsAsync();
      const locationStatus = await Location.getForegroundPermissionsAsync();
      const micStatus = await Audio.getPermissionsAsync();
      const contactsStatus = await Contacts.getPermissionsAsync();

      return {
        camera: cameraStatus.granted || cameraStatus.status === 'granted',
        mediaLibrary: mediaStatus.granted || mediaStatus.status === 'granted',
        location: locationStatus.granted || locationStatus.status === 'granted',
        microphone: micStatus.granted || micStatus.status === 'granted',
        contacts: contactsStatus.granted || contactsStatus.status === 'granted',
      };
    } catch (error) {
      console.log('🔐 [MediaService] Permission check failed, defaulting to true:', error);
      return {
        camera: true,
        mediaLibrary: true,
        location: true,
        microphone: true,
        contacts: true,
      };
    }
  }

  // Permission request
  static async requestPermission(type: 'camera' | 'mediaLibrary' | 'location' | 'microphone' | 'contacts'): Promise<boolean> {
    console.log(`🔐 [MediaService] Requesting ${type} permission`);
    try {
      switch (type) {
        case 'camera': {
          const current = await ImagePicker.getCameraPermissionsAsync();
          if (current.granted || current.status === 'granted') return true;
          const result = await ImagePicker.requestCameraPermissionsAsync();
          return result.granted || result.status === 'granted';
        }
        case 'mediaLibrary': {
          const current = await ImagePicker.getMediaLibraryPermissionsAsync();
          if (current.granted || current.status === 'granted') return true;
          const result = await ImagePicker.requestMediaLibraryPermissionsAsync();
          return result.granted || result.status === 'granted';
        }
        case 'location': {
          const current = await Location.getForegroundPermissionsAsync();
          if (current.granted || current.status === 'granted') return true;
          const result = await Location.requestForegroundPermissionsAsync();
          return result.granted || result.status === 'granted';
        }
        case 'microphone': {
          const current = await Audio.getPermissionsAsync();
          if (current.granted || current.status === 'granted') return true;
          const result = await Audio.requestPermissionsAsync();
          return result.granted || result.status === 'granted';
        }
        case 'contacts': {
          const current = await Contacts.getPermissionsAsync();
          if (current.granted || current.status === 'granted') return true;
          const result = await Contacts.requestPermissionsAsync();
          return result.granted || result.status === 'granted';
        }
        default:
          return true;
      }
    } catch (error) {
      console.log(`🔐 [MediaService] Request ${type} permission failed, defaulting to false:`, error);
      return false;
    }
  }
}